/**
 * Direct Pricing Tab - Calculate prices with fixed margin
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert, HelpTooltip } from '../../components/ui/HelpGuide';
import { Modal } from '../../components/ui/modal';
import { 
  Calculator, 
  CheckCircle,
  RefreshCw,
  ArrowRight,
  Package,
  DollarSign
} from 'lucide-react';

export default function DirectPricing({ categories, costVariations, formatCurrency }) {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [applying, setApplying] = useState(false);
  
  const [categoryId, setCategoryId] = useState('all');
  const [marginPercentage, setMarginPercentage] = useState(30);
  const [applyVariations, setApplyVariations] = useState(true);
  
  const [calculations, setCalculations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      
      const payload = {
        margin_percentage: marginPercentage,
        apply_variations: applyVariations
      };
      
      if (categoryId && categoryId !== 'all') {
        payload.category_id = categoryId;
      }
      
      const response = await api.post('/pricing/calculate/direct', payload);
      
      if (response.data?.success) {
        setCalculations(response.data.data.calculations || []);
        setSummary(response.data.data.summary || null);
        setSelectedProducts(response.data.data.calculations?.map(c => c.product_id) || []);
        toast.success(`${response.data.data.calculations?.length || 0} produtos calculados`);
      }
    } catch (err) {
      toast.error('Erro ao calcular preços');
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === calculations.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(calculations.map(c => c.product_id));
    }
  };

  const handleApplyPrices = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    try {
      setApplying(true);
      
      const calcsToApply = calculations
        .filter(c => selectedProducts.includes(c.product_id))
        .map(c => ({
          product_id: c.product_id,
          new_price: c.suggested_price
        }));
      
      const response = await api.post('/pricing/apply', {
        calculations: calcsToApply
      });
      
      if (response.data?.success) {
        toast.success(`${response.data.data.updated_count} preços atualizados!`);
        setShowConfirmModal(false);
        setCalculations([]);
        setSummary(null);
      }
    } catch (err) {
      toast.error('Erro ao aplicar preços');
    } finally {
      setApplying(false);
    }
  };

  const selectedSummary = {
    count: selectedProducts.length,
    totalCurrent: calculations
      .filter(c => selectedProducts.includes(c.product_id))
      .reduce((acc, c) => acc + c.current_price, 0),
    totalSuggested: calculations
      .filter(c => selectedProducts.includes(c.product_id))
      .reduce((acc, c) => acc + c.suggested_price, 0)
  };

  return (
    <div className="space-y-6">
      <HelpAlert variant="tip" title="Precificação Direta">
        Defina uma margem de lucro percentual. O sistema calculará o preço de venda 
        somando o custo do produto + variações de custo + margem desejada.
      </HelpAlert>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurar Cálculo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Categoria
                <HelpTooltip>
                  Selecione uma categoria ou "Todas" para calcular todos os produtos
                </HelpTooltip>
              </Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Margin Slider */}
            <div className="space-y-2 col-span-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1">
                  Margem de Lucro
                  <HelpTooltip>
                    Porcentagem de lucro sobre o custo total (custo + variações)
                  </HelpTooltip>
                </Label>
                <Badge variant="secondary" className="text-lg font-bold">
                  {marginPercentage}%
                </Badge>
              </div>
              <Slider
                value={[marginPercentage]}
                onValueChange={([v]) => setMarginPercentage(v)}
                min={5}
                max={100}
                step={5}
                className="py-4"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>5%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="apply-variations"
                checked={applyVariations}
                onCheckedChange={setApplyVariations}
              />
              <Label htmlFor="apply-variations" className="flex items-center gap-1">
                Aplicar variações de custo
                <HelpTooltip>
                  Inclui custos de embalagem, gás, impostos, etc. configurados nas variações
                </HelpTooltip>
              </Label>
            </div>
            
            {costVariations.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {costVariations.length} variações ativas
              </div>
            )}
          </div>

          <Button 
            onClick={handleCalculate} 
            disabled={calculating}
            className="w-full"
            data-testid="calculate-direct-btn"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {calculating ? 'Calculando...' : 'Calcular Preços'}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {calculations.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary?.total_products || 0}</div>
                <div className="text-sm text-muted-foreground">Produtos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary?.margin_percentage || 0}%</div>
                <div className="text-sm text-muted-foreground">Margem</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{formatCurrency(summary?.total_current_revenue || 0)}</div>
                <div className="text-sm text-muted-foreground">Preço Atual Total</div>
              </CardContent>
            </Card>
            <Card className="border-green-500/50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-500">{formatCurrency(summary?.total_suggested_revenue || 0)}</div>
                <div className="text-sm text-muted-foreground">Preço Sugerido Total</div>
              </CardContent>
            </Card>
          </div>

          {/* Products Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Produtos Calculados</CardTitle>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedProducts.length === calculations.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <Label>Selecionar todos</Label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y max-h-96 overflow-y-auto">
                {calculations.map((calc) => (
                  <div key={calc.product_id} className="flex items-center gap-4 py-3">
                    <Checkbox
                      checked={selectedProducts.includes(calc.product_id)}
                      onCheckedChange={() => handleSelectProduct(calc.product_id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{calc.product_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {calc.sku && `SKU: ${calc.sku}`}
                      </div>
                    </div>
                    <div className="text-center w-24">
                      <div className="text-xs text-muted-foreground">Custo Base</div>
                      <div className="text-sm">{formatCurrency(calc.base_cost)}</div>
                    </div>
                    <div className="text-center w-24">
                      <div className="text-xs text-muted-foreground">+ Variações</div>
                      <div className="text-sm">{formatCurrency(calc.variation_cost)}</div>
                    </div>
                    <div className="text-center w-24">
                      <div className="text-xs text-muted-foreground">= Custo Total</div>
                      <div className="text-sm font-medium">{formatCurrency(calc.total_cost)}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="text-center w-24">
                      <div className="text-xs text-muted-foreground">Atual</div>
                      <div className="text-sm">{formatCurrency(calc.current_price)}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-green-500" />
                    <div className="text-center w-28">
                      <div className="text-xs text-muted-foreground">Sugerido</div>
                      <Badge variant="outline" className="bg-green-500/10 text-green-600 font-bold">
                        {formatCurrency(calc.suggested_price)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Apply Button */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <span className="font-medium">{selectedProducts.length}</span> produtos selecionados
                <span className="mx-2">•</span>
                <span>De {formatCurrency(selectedSummary.totalCurrent)}</span>
                <span className="mx-2">→</span>
                <span className="text-green-500 font-medium">{formatCurrency(selectedSummary.totalSuggested)}</span>
              </div>
              <Button 
                onClick={() => setShowConfirmModal(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="apply-prices-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aplicar Preços Selecionados
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm Modal */}
      <Modal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirmar Aplicação de Preços"
        description="Os preços dos produtos selecionados serão atualizados."
        confirmText="Confirmar e Aplicar"
        loading={applying}
        onConfirm={handleApplyPrices}
      >
        <HelpAlert variant="warning">
          Esta ação irá atualizar o preço de venda de {selectedProducts.length} produtos.
          O histórico de preços será mantido.
        </HelpAlert>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">Preço Total Atual</div>
            <div className="text-xl font-bold">{formatCurrency(selectedSummary.totalCurrent)}</div>
          </div>
          <div className="p-4 bg-green-500/10 rounded-lg">
            <div className="text-sm text-muted-foreground">Preço Total Novo</div>
            <div className="text-xl font-bold text-green-600">{formatCurrency(selectedSummary.totalSuggested)}</div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
